import React from 'react';
import { graphql } from 'gatsby';
import { MantineProvider } from '@mantine/core';
import "@mantine/core/styles.css";
import { PageMetaFields } from '../interface/pageMetaFields.interface';
import * as headerStyles from '../components/Header/header.module.scss';
import Seo from '../components/Seo/Seo';
import Header from '../components/Header/Index';
import Footer from '../components/Footer/Index';
import CaseStudyBanner, { CaseStudyBannerInterface } from '../components/CaseStudyV2/CaseStudyBanner/CaseStudyBanner';
import CaseStudyOurApproach, { CaseStudyOurApproachProps } from '../components/CaseStudyV2/CaseStudyOurApproach/CaseStudyOurApproach';
import CaseStudyDesignHighlights, { CaseStudyHighlightsProps } from '../components/CaseStudyV2/CaseStudyDesignHighlights/CaseStudyDesignHighlights';
import FinalProductShowcase, { FinalProductProps } from '../components/CaseStudyV2/FinalProductShowcase/FinalProductShowcase';
import CaseStudyPostProduction, { PostProductionInterface } from '../components/CaseStudyV2/CaseStudyPostProduction/CaseStudyPostProduction';
import CaseStudyClientsAndPartners, { CaseStudyClientsData } from '../components/CaseStudyV2/CaseStudyClientsAndPartners/CaseStudyClientsAndPartners';
import { CaseStudyTestimonialCardProps } from '../components/CaseStudyV2/CaseStudyClientsAndPartners/Component/CaseStudyTestimonialCard';
import LetsTalk from '../components/Home/LetsTalk/LetsTalk';
import UpcomingCaseStudies, { UpcomingCaseStudiesProps } from '../components/CaseStudyV2/UpcomingCaseStudies/UpcomingCaseStudies';



const videoSources = {
  webm: '../../images/arVrWebsiteDesign/ar-vr-website-video.mp4',
  mp4: '../../images/arVrWebsiteDesign/ar-vr-website-video.mp4',
  posterImage: '../../images/uiUxDesignCaseStudy/trust-the-process.jpg',
  altText: 'Final Product Showcase',
};

interface CaseStudiesV2 {
  caseStudiesBanner: CaseStudyBannerInterface | null;
  caseStudiesOurApproach: CaseStudyOurApproachProps | null;
  caseStudiesDesignHighlights: CaseStudyHighlightsProps | null;
  caseStudiesProductShowcase: FinalProductProps | null;
  caseStudiesPostProduction: PostProductionInterface | null;
  caseStudiesUpcoming: UpcomingCaseStudiesProps | null;
  caseStudiesClientsAndPartners: CaseStudyClientsData | null;
}
interface WpCaseStudyV2 {
  id: string | null;
  title: string | null;
  slug: string | null;
  pageMetaFields: PageMetaFields | null;
  caseStudiesV2: CaseStudiesV2 | null;
}
interface TestimonialsInterface {
  edges: CaseStudyTestimonialCardProps[] | null;
}
interface PostTypeResponse {
  wpCaseStudyV2: WpCaseStudyV2 | null;
  testimonials: TestimonialsInterface | null;
}

export default function SingleCaseStudy({ data }: { data: PostTypeResponse | null }) {

  const apiData = data?.wpCaseStudyV2;

  return (
    <MantineProvider>
      <main className="main_canvas">
        <div className={headerStyles.caseStudyV2}>
          <Seo
            title={apiData?.pageMetaFields?.pageTitle}
            description={apiData?.pageMetaFields?.metaDescriptions}
            canonical={`https://www.designstudiouiux.com/case-study/${apiData?.slug}/`}
            breadcrumbs={apiData?.pageMetaFields?.bredcrumbLinks?.map(i => ({ slug: i?.pageUrl, name: i?.pageTitle }))}
            noIndex={Boolean(apiData?.pageMetaFields?.noindex)}
          />
          <Header blogHeader={false} />
          <CaseStudyBanner 
            data={{
              breadcrumb: apiData?.pageMetaFields?.bredcrumbLinks,
              bannerData: apiData?.caseStudiesV2?.caseStudiesBanner as CaseStudyBannerInterface
            }}
          />
          <CaseStudyOurApproach data={data?.wpCaseStudyV2?.caseStudiesV2?.caseStudiesOurApproach as CaseStudyOurApproachProps} />
          <CaseStudyDesignHighlights data={data?.wpCaseStudyV2?.caseStudiesV2?.caseStudiesDesignHighlights as CaseStudyHighlightsProps} />
          <FinalProductShowcase
            data={{
              context: apiData?.caseStudiesV2?.caseStudiesProductShowcase as FinalProductProps,
              videoSources: videoSources
            }} 
          />
          <CaseStudyPostProduction data={apiData?.caseStudiesV2?.caseStudiesPostProduction as PostProductionInterface} />
          <UpcomingCaseStudies data={apiData?.caseStudiesV2?.caseStudiesUpcoming as UpcomingCaseStudiesProps} />
          <CaseStudyClientsAndPartners
            clientsData={apiData?.caseStudiesV2?.caseStudiesClientsAndPartners as CaseStudyClientsData }
            testimonialsData={data?.testimonials?.edges as CaseStudyTestimonialCardProps[]}
          />
          <LetsTalk />
        </div>
      </main>
      <Footer />
    </MantineProvider>
  )
}

export const query = graphql`
  query ($slug: String!) {
    wpCaseStudyV2(slug: {eq: $slug}) {
      id
      title
      slug
      pageMetaFields {
        pageTitle
        metaDescriptions
        bredcrumbLinks {
          pageTitle
          pageUrl
        }
        noindex
      }
      caseStudiesV2 {
        caseStudiesBanner {
          heading
          elementImage {
            altText
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData (
                    quality: 90
                    formats: [AUTO, WEBP, AVIF]
                    layout: CONSTRAINED
                    placeholder: BLURRED
                  )
                }
              }
            }
          }
          description
          subHeadingTwo
          contextList {
            label
            message
          }
          sectionImage {
            altText
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData (
                    quality: 90
                    formats: [AUTO, WEBP, AVIF]
                    layout: CONSTRAINED
                    placeholder: BLURRED
                  )
                }
              }
            }
          }
          context {
            heading
            description
          }
        }
        caseStudiesOurApproach {
          heading {
            headingChild
          }
          description
          context {
            sectionImage {
              altText
              image {
                localFile {
                  childImageSharp {
                    gatsbyImageData (
                      quality: 90
                      formats: [AUTO, WEBP, AVIF]
                      layout: CONSTRAINED
                      placeholder: BLURRED
                    )
                  }
                }
              }
            }
            heading
            subHeading
            description
          }
        }
        caseStudiesDesignHighlights {
          heading {
            headingChild
          }
          description
          sectionImage {
            altText
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData (
                    quality: 90
                    formats: [AUTO, WEBP, AVIF]
                    layout: CONSTRAINED
                    placeholder: BLURRED
                  )
                }
              }
            }
          }
          descriptionTwo
          context {
            heading
            description
            descriptionTwo
            sectionImage {
              altText
              image {
                localFile {
                  childImageSharp {
                    gatsbyImageData (
                      quality: 90
                      formats: [AUTO, WEBP, AVIF]
                      layout: CONSTRAINED
                      placeholder: BLURRED
                    )
                  }
                }
              }
            }
          }
        }
        caseStudiesProductShowcase {
          heading {
            headingChild
          }
          description
          descriptionRightSide
          button {
            buttonContext
            slug
          }
          context {
            label
            description
          }
          testimonial {
            quote
            ownerData
          }
          sectionImage {
            altText
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          thumbnail {
            thumbnailOne {
              altText
              image {
                localFile {
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
            thumbnailTwo {
              altText
              image {
                localFile {
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
            thumbnailThree {
              context
              link
            }
          }
          descriptionTwo
        }
        caseStudiesPostProduction {
          heading {
            headingChild
          }
          description
        }
        caseStudiesUpcoming {
          heading {
            headingChild
          }
          button {
            buttonContext
            slug
          }
        }
        caseStudiesClientsAndPartners {
          heading {
            headingChild
          }
          button {
            buttonContext
            slug
          }
        }
      }
    }
    testimonials: allWpTestimonial {
      edges {
        node {
          id
          title
          content
          date
          testimonials {
            professions
          }
          featuredImage {
            node {
              altText
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    quality: 90
                    formats: [AUTO, WEBP, AVIF]
                    layout: CONSTRAINED
                    placeholder: BLURRED
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`;
